import type { Permission } from '@contexts/permissions-provider/api/fetch-permissions/fetchPermissions.types';

import { fetchPermissions } from '@contexts/permissions-provider/api/fetch-permissions/fetchPermissions';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { hasRequiredPermissions } from '../use-salesforce-redirect/useSalesforceRedirect.utils';

/**
 * Fetches global permissions (no org header) and checks whether
 * the user has all permissions required for the SF redirect flow.
 *
 * Uses a dedicated query key to avoid collisions with the global
 * PermissionsProvider cache (which is org-scoped for Manage users).
 *
 * Returns `null` while loading, `true`/`false` once resolved.
 */
export function usePermissionsCheck(
  isAuthenticated: boolean,
  hasParams: boolean
): boolean | null {
  const { data, isLoading, isError } = useQuery<Permission[]>({
    queryKey: ['sf-redirect-permissions'],
    queryFn: () => fetchPermissions(),
    enabled: isAuthenticated && hasParams,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    if (!isAuthenticated || !hasParams) return null;
    if (isLoading) return null;
    if (isError) return false;
    if (!data) return null;
    const names = data.map(p => p.name);
    return hasRequiredPermissions(names);
  }, [isAuthenticated, hasParams, isLoading, isError, data]);
}
